<script setup>
import { ref } from "vue";
import { useUserStore } from "../stores/user.js";
import { supabase } from "../lib/supabase.js";
import { useRouter } from "vue-router";
import {
  createUserFlashcards,
  updateUserAbilityDistribution,
  GAUSSIAN_DEFAULTS_BY_LEVEL,
  setOnboardingStartingRank,
} from "../Scripts/dbFunctions.js";
import {
  listNativeLanguages,
  listTargetLanguages,
  assertValidNativeLanguage,
  assertValidTargetLanguage,
} from "../lib/languages.js";

// const userStore = useUserStore();
const router = useRouter();
const isLoading = ref(false);
const error = ref("");
const selectedNativeLanguage = ref(null);
const selectedLanguage = ref(null);
const selectedProficiency = ref(null);
const userStore = useUserStore();

const nativeLanguages = listNativeLanguages();
const languages = listTargetLanguages();

const handleContinue = async () => {
  // Reset previous error
  error.value = "";

  // Basic presence validation
  if (!selectedNativeLanguage.value) {
    error.value = "Please select your native language";
    return;
  }
  if (!selectedLanguage.value) {
    error.value = "Please select a language to learn";
    return;
  }
  if (!selectedProficiency.value) {
    error.value = "Please select your proficiency level";
    return;
  }

  // Validate codes using central catalog
  try {
    assertValidNativeLanguage(selectedNativeLanguage.value);
    assertValidTargetLanguage(selectedLanguage.value);
  } catch (e) {
    error.value = e?.message || "Invalid language selection";
    return;
  }

  isLoading.value = true;
  try {
    // Save native language first
    const nativeSaved = await userStore.setNativeLanguage(
      selectedNativeLanguage.value
    );
    if (!nativeSaved) {
      error.value = "Failed to save native language preference";
      console.error("Error saving native language preference");
      return;
    }

    // Then save target (learning) language and initialize user flashcards
    if (await userStore.setLanguage(selectedLanguage.value)) {
      const { error: dbError } = await createUserFlashcards(
        selectedLanguage.value,
        userStore.user.id
      );
      if (dbError) {
        error.value = "Failed to update user flashcard language";
        console.error("Error updating user flashcard language:", dbError);
        return;
      }
      // Initialize Rasch prior for proficiency (non-blocking if it fails)
      const levelKey = selectedProficiency.value;
      const defaults = GAUSSIAN_DEFAULTS_BY_LEVEL[levelKey];
      if (defaults) {
        const { success, error: distErr } = await updateUserAbilityDistribution(
          defaults.mean,
          defaults.var,
          selectedLanguage.value
        );
        if (!success) {
          console.error(
            "Error updating language_ability_distribution:",
            distErr
          );
        }
      }
      // If user selected pre-beginner, set starting rank to 0 immediately and initialize FSRS schedule
      if (selectedProficiency.value === "pre-beginner") {
        const { success, error: rankErr } = await setOnboardingStartingRank(
          selectedLanguage.value,
          0
        );
        if (!success) {
          console.error("Error setting starting_word_rank to 0:", rankErr);
        } else {
          // Initialize scheduling for cards up to starting_word_rank (same as onboarding test completion)
          try {
            const { data: auth } = await supabase.auth.getUser();
            const uid = auth?.user?.id || null;
            if (!uid) {
              console.warn(
                "[OnboardingView] skipping FSRS schedule: missing user id"
              );
            } else {
              const payload = {
                user_id: uid,
                language_code: selectedLanguage.value,
                starting_word_rank: 0,
                fsrs_defaults: { difficulty: 1, stability: 25, state: 3 },
              };
              supabase.functions
                .invoke("apply-fsrs-schedule", { body: payload })
                .then((res) => {
                  console.log(
                    "[OnboardingView] Edge FSRS schedule result",
                    res?.data ?? res
                  );
                })
                .catch((err) => {
                  console.error(
                    "[OnboardingView] Edge FSRS schedule error",
                    err
                  );
                });
            }
          } catch (e) {
            console.error("[OnboardingView] Edge FSRS schedule exception", e);
          }
        }
      }
      router.push("/learn");
    } else {
      error.value = "Failed to save language preference";
      console.error("Error saving language preference:", error.value);
    }
  } catch (e) {
    error.value = e?.message || "Unexpected error saving preferences";
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="onboarding-container">
    <form @submit.prevent="handleContinue" class="onboarding-form">
      <h2>Tell us your native language</h2>

      <div class="form-group">
        <label>Native language</label>
        <select
          v-model="selectedNativeLanguage"
          class="language-select"
          required
        >
          <option disabled value="">Choose your native language...</option>
          <option
            v-for="lang in nativeLanguages"
            :key="lang.code"
            :value="lang.code"
          >
            {{ lang.name }}
          </option>
        </select>
      </div>

      <h2>Select the language you want to learn</h2>

      <div class="form-group">
        <label>Language to learn</label>
        <select
          v-model="selectedLanguage"
          class="language-select"
          required
          :disabled="!selectedNativeLanguage"
        >
          <option disabled value="">Choose a language...</option>
          <option v-for="lang in languages" :key="lang.code" :value="lang.code">
            {{ lang.name }}
          </option>
        </select>
      </div>

      <h2>How proficient are you in your target language?</h2>

      <div class="form-group">
        <label>Proficiency level</label>
        <select
          v-model="selectedProficiency"
          class="language-select"
          required
          :disabled="!selectedLanguage"
        >
          <option disabled value="">Choose your proficiency...</option>
          <option value="pre-beginner">Pre-beginner</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      <button
        type="submit"
        :disabled="
          !selectedNativeLanguage ||
          !selectedLanguage ||
          !selectedProficiency ||
          isLoading
        "
        class="continue-button"
      >
        {{ isLoading ? "Saving..." : "Continue" }}
      </button>

      <p v-if="error" class="error-message">{{ error }}</p>
    </form>
  </div>
</template>

<style scoped>
.onboarding-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.onboarding-form {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
}

.language-select {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  appearance: none;
  background: var(--hover-bg);
  color: var(--text-color);
}

.continue-button {
  width: 100%;
  padding: 1rem;
  background-color: #99b5d3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
}

.continue-button:hover:not(:disabled) {
  background-color: #7d9cbb;
}

.continue-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #ff4444;
  margin: 1rem 0;
  text-align: center;
}
@media (max-width: 480px) {
  .onboarding-container {
    padding: 16px;
  }

  .onboarding-form {
    max-width: 360px;
    padding: 1.25rem;
  }
}
</style>
